import { ref, computed } from 'vue';
import { checkMemberBenefit } from '@/jmHttp/api/common';
import { NEED_CHANGE_IDENTITY, SUMMARY_TYPE_MAP, MEMBER_TEXT_I18N_MAP } from '@/constants';
import { checkUserBenefit } from '@/jmHttp/api/summary';
import i18n from '@/plugins/i18n';

/**
 * 用于检查当前用户权限状态并生成对应提示信息
 * @param {string} benefitFuncCode - 功能权益编码，用于查询对应的成员权益
 * @param {boolean} useCode - 是否根据code的状态设置提示信息：0 纪要没权限；1 提醒开会员；2 提醒付牛币
 * @returns {Object} 返回包含权限状态、提示信息和操作方法的对象:
 *   - haveAuth: 当前用户是否拥有权限
 *   - tipText: 计算生成的权限提示文本
 *   - tipConfirmRef: 提示确认组件引用
 *   - handleClickAuth: 处理权限申请点击事件的方法
 */
export async function useAuthTip(benefitFuncCode, roadshowId, result, useCode = false) {
  // 响应式状态：权限状态、身份切换需求、商品信息
  const haveAuth = ref(false);
  const needChangeIdentity = ref(0);
  const goodsMessage = ref({});
  const vipBenefit = ref({
    buyType: 1 // 是否需要支付牛币：0 不需要；1 需要
  });

  // 计算属性：判断需要切换的身份类型
  const TO_PERSONAL = computed(() => needChangeIdentity.value === NEED_CHANGE_IDENTITY.TO_PERSONAL);
  const TO_ENTERPRISE = computed(() => needChangeIdentity.value === NEED_CHANGE_IDENTITY.TO_ENTERPRISE);

  // 动态生成提示文本，根据身份切换需求显示不同版本提示
  const tipText = computed(() => {
    const name = MEMBER_TEXT_I18N_MAP[goodsMessage.value?.goodsDesc]
      ? i18n.global.t('common.member.professional_Member')
      : goodsMessage.value?.goodsDesc;
    if (TO_PERSONAL.value) {
      return {
        authTips: `${i18n.global.t('common.tip.open_personal_member_view_with_goods_desc', {
          name
        })}`,
        authTips2: `(${i18n.global.t('common.tip.contact_admin')})`,
        subAuthTips: '',
        needPayNbi: false
      };
    }
    if (TO_ENTERPRISE.value) {
      return {
        authTips: `${i18n.global.t('common.tip.open_enterprise_member_view_with_goods_desc', {
          name
        })}`,
        authTips2: `(${i18n.global.t('common.tip.contact_admin')})`,
        subAuthTips: '',
        needPayNbi: true
      };
    }
    // 提醒开会员
    if (useCode && result.code === SUMMARY_TYPE_MAP.OPEN_MEMBERSHIP) {
      return {
        authTips: i18n.global.t('common.tip.open_membership', {
          name: result?.goodsMessage?.goodsDesc || i18n.global.t('common.noun.membership')
        }),
        authTips2: '',
        subAuthTips: '',
        needPayNbi: true
      };
    }
    // 提醒付牛币
    if (useCode && result.code === SUMMARY_TYPE_MAP.PAY_COIN) {
      return {
        authTips: i18n.global.t('member.discountTypeTips.discount', {
          coin: result?.needPayNbiNum || 0
        }),
        authTips2: '',
        subAuthTips: '',
        needPayNbi: true
      };
    }
    return {
      authTips: `${i18n.global.t('common.tip.open_with_goods_desc', { name })}`,
      authTips2: `(${i18n.global.t('common.tip.contact_admin')})`,
      subAuthTips: '',
      needPayNbi: true
    };
  });

  // 提示框引用和处理授权点击事件
  const tipConfirmRef = ref(null);
  const handleClickAuth = () => {
    goodsMessage.value.goodsDesc && tipConfirmRef.value.message();
  };

  // 发起权限验证请求，更新权限相关状态
  if (result) {
    haveAuth.value = result.haveAuth || false;
    vipBenefit.value = result.vipBenefit || {};
    goodsMessage.value = result.goodsMessage || {};
    needChangeIdentity.value = result.needChangeIdentity || 0;
  } else if (roadshowId) {
    const res = await checkUserBenefit({ roadshowId, benefitFuncCode });
    haveAuth.value = res?.data?.haveAuth || false;
    vipBenefit.value = res?.data?.vipBenefit || {};
    goodsMessage.value = res?.data?.goodsMessage || {};
    needChangeIdentity.value = res?.data?.needChangeIdentity || 0;
  } else {
    const res = await checkMemberBenefit({ benefitFuncCode });
    haveAuth.value = res?.data?.haveAuth || false;
    vipBenefit.value = res?.data?.vipBenefit || {};
    goodsMessage.value = res?.data?.goodsMessage || {};
    needChangeIdentity.value = res?.data?.needChangeIdentity || 0;
  }

  return {
    haveAuth,
    vipBenefit,
    goodsMessage,
    tipText,
    tipConfirmRef,
    needChangeIdentity,
    handleClickAuth
  };
}
